<template>
  <UIDropdown
    v-if="activeModel"
    :items="featuredModels"
    class="DropdownModel"
    contentClass="DropdownModel__content"
    :offset="[0, 8]"
    :width="'376px'"
    :maxWidth="'376px'"
    :interactive="true"
    :hideOnClick="!isChildDropdownOpen"
    @onSelect="setActiveModel"
  >
    <template #toggle>
      <UIDropdownToggle class="DropdownModel__trigger">
        <UILoader v-if="isFetching" class="DropdownModel__loader" />
        <DropdownModelItem
          v-else
          :key="activeModel.slug"
          :isTopbarDisplay="true"
          :model="activeModel"
          class="DropdownModel__active-item"
        />
      </UIDropdownToggle>
    </template>

    <template #before-content>
      <div class="DropdownModel__header">
        <div class="DropdownModel__header-label">Models</div>
        <UITooltip placement="right">
          <template #content>
            Switch between different models in <br />EverGPT depending on your
            needs
          </template>
        </UITooltip>
      </div>
    </template>

    <template #option="{ option }">
      <UITooltip
        v-if="option.description_long"
        placement="right"
        :offset="[0, 24]"
      >
        <template #content>
          {{ option.description_long }}
        </template>

        <DropdownModelItem
          class="DropdownModel__list-item"
          :model="option"
          :isSelected="option.slug === activeModel.slug"
        />
      </UITooltip>

      <DropdownModelItem
        v-else
        class="DropdownModel__list-item"
        :model="option"
        :isSelected="option.slug === activeModel.slug"
      />
    </template>

    <template #after-content="{ hide }">
      <UIDropdown
        v-if="moreModels.length"
        :items="moreModels"
        class="DropdownModel__more-dropdown"
        placement="right-start"
        :popperOptions="moreDropdownPopperOptions"
        :offset="[0, 12]"
        trigger="mouseenter"
        contentClass="DropdownModel__more-content"
        @onSelect="onSelectMoreModel($event, hide)"
        @trigger="isChildDropdownOpen = true"
        @untrigger="isChildDropdownOpen = false"
      >
        <template #toggle>
          <div class="DropdownModel__more-trigger">
            <span class="DropdownModel__more-label">More models</span>
            <UIIcon name="chevronRight" class="DropdownModel__more-icon" />
          </div>
        </template>

        <template #option="{ option }">
          <UITooltip
            v-if="option.description_long"
            placement="right"
            :offset="[0, 24]"
          >
            <template #content>
              {{ option.description_long }}
            </template>

            <DropdownModelItem
              class="DropdownModel__list-item"
              :model="option"
              :isSelected="option.slug === activeModel.slug"
            />
          </UITooltip>
          <DropdownModelItem
            v-else
            class="DropdownModel__list-item"
            :model="option"
            :isSelected="option.slug === activeModel.slug"
          />
        </template>
      </UIDropdown>
    </template>
  </UIDropdown>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'

import { storeToRefs } from 'pinia'

import type { IModel } from '@/apiClient/types/model'
import { useModelStore } from '@/store/model'
import { splitModelsForChatDropdown } from '@/utils/chatModelOrder'
import UIDropdown from '@/views/components/ui/dropdowns/UIDropdown.vue'
import UIDropdownToggle from '@/views/components/ui/dropdowns/UIDropdownToggle.vue'
import UIIcon from '@/views/components/ui/icons/UIIcon.vue'
import UILoader from '@/views/components/ui/loaders/UILoader.vue'
import UITooltip from '@/views/components/ui/tooltips/UITooltip.vue'

import DropdownModelItem from './DropdownModelItem.vue'

const modelStore = useModelStore()
const { selectableModels, activeModel, isFetching } = storeToRefs(modelStore)
const { setActiveModel } = modelStore

const isChildDropdownOpen = ref(false)

const chatDropdownGroups = computed(() =>
  splitModelsForChatDropdown(selectableModels.value),
)
const featuredModels = computed(() => chatDropdownGroups.value.featured)
const moreModels = computed(() => chatDropdownGroups.value.more)

/** Prefer right-start but flip to left-start (and others) when no viewport space */
const moreDropdownPopperOptions = {
  modifiers: [
    {
      name: 'flip',
      options: {
        fallbackPlacements: [
          'left-start',
          'right-end',
          'left-end',
          'bottom-start',
          'top-start',
        ],
      },
    },
  ],
}

function onSelectMoreModel(model: IModel, hideParent: () => void) {
  hideParent()
  setActiveModel(model)
}
</script>

<style scoped lang="scss">
.DropdownModel {
  .DropdownModel__loader {
    --size: 1.25rem;
    margin: 0 1rem;
  }

  .DropdownModel__trigger {
    padding-right: 1rem;
    border-radius: 4px;

    &:hover {
      background-color: $color-neutral-5;
    }
  }

  .DropdownModel__active-item {
    padding: 0.5rem 0.5rem 0.5rem 1rem;
    align-items: center;
  }
}
</style>

<style lang="scss">
.DropdownModel__header {
  display: flex;
  gap: 0.25rem;
  align-items: center;
  padding: 0.5rem 0.75rem 0.5rem 0.75rem;
}

.DropdownModel__header-label {
  @include font-size-12px;
  color: $color-neutral-150;
  font-weight: 700;
}

.DropdownModel__more-dropdown {
  padding: 1rem;
  border-top: 1px solid $color-neutral-10;
  cursor: pointer;

  &:hover {
    background-color: $color-neutral-5;
  }
}

.DropdownModel__more-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  cursor: pointer;
  color: $color-neutral-140;
}

.DropdownModel__more-dropdown {
  width: 100%;
}

.DropdownModel__more-label {
  @include font-size-14px;
}

.DropdownModel__more-icon {
  --size: 1rem;
  color: $color-neutral-100;
}
</style>
